import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { resolveImg } from "@/contexts/SettingsContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function DynamicPage() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [page, setPage] = useState(undefined); // undefined = loading, null = not found

  useEffect(() => {
    setPage(undefined);
    api.get(`/pages/${slug}`).then((r) => setPage(r.data)).catch(() => setPage(null));
  }, [slug]);

  const tr = page && page.translations
    ? (page.translations[lang] && page.translations[lang].title ? page.translations[lang]
      : page.translations[lang] && (page.translations[lang].blocks || []).length ? page.translations[lang]
      : page.translations.de || page.translations[lang] || {})
    : {};
  const blocks = (tr && tr.blocks) || [];

  return (
    <div className="relative min-h-screen">
      <div className="vl-noise" />
      <Header />
      {page === undefined ? (
        <div className="py-40 text-center"><Loader2 className="animate-spin text-[var(--vl-gold)] mx-auto" /></div>
      ) : page === null ? (
        <section className="max-w-3xl mx-auto px-6 pt-40 pb-24 text-center" data-testid="dynamic-page-404">
          <h1 className="font-serif-display text-4xl text-[var(--vl-ivory)]">{t("page_not_found")}</h1>
          <Link to="/" className="vl-btn-gold rounded-full px-7 py-3.5 text-sm tracking-widest uppercase font-medium inline-block mt-8">{t("nav_home")}</Link>
        </section>
      ) : (
        <article data-testid="dynamic-page">
          {page.hero_image && (
            <div className="relative h-72 sm:h-96 w-full overflow-hidden">
              <img src={resolveImg(page.hero_image)} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--vl-bg)] via-[rgba(0,0,0,0.3)] to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-6 sm:px-8 pb-10">
                <h1 className="font-serif-display text-4xl sm:text-5xl font-light img-caption">{tr.title}</h1>
              </div>
            </div>
          )}
          <section className="max-w-3xl mx-auto px-6 sm:px-8 pt-16 pb-24">
            {!page.hero_image && (
              <h1 className="font-serif-display text-4xl sm:text-5xl font-light text-[var(--vl-ivory)] mb-10">{tr.title}</h1>
            )}
            <div className="space-y-7">
              {blocks.map((blk) => {
                if (blk.type === "heading") return <h2 key={blk.id} className="font-serif-display text-2xl sm:text-3xl text-[var(--vl-ivory)] mt-4">{blk.text}</h2>;
                if (blk.type === "image") return (
                  <figure key={blk.id} className="my-2">
                    <img src={resolveImg(blk.url)} alt={blk.caption || ""} className="w-full rounded-2xl border border-[var(--vl-border)]" />
                    {blk.caption && <figcaption className="text-xs text-[var(--vl-stone)] mt-2 text-center">{blk.caption}</figcaption>}
                  </figure>
                );
                return <div key={blk.id} className="vl-rte-content text-[var(--vl-stone)] leading-relaxed" dangerouslySetInnerHTML={{ __html: blk.html || "" }} />;
              })}
            </div>
          </section>
        </article>
      )}
      <Footer />
    </div>
  );
}
